import React, { useMemo } from 'react';
import { Col, Row } from 'antd';
import { getDimensionValue } from 'components/DimensionValue';
import { useMatomoGoalNames } from 'hooks/useMatomoGoalNames';
import { useWebsiteCurrency } from 'hooks/useWebsiteCurrency';
import useFormatMessage from 'hooks/useFormatMessage';
import SegmentTag from 'components/SegmentTag';
import { getFormatter } from 'shared/utils/formatter';
import { getMetricPrecision } from 'shared/utils/metricPrecision';
import { metricSupportsShareOfTotal } from 'shared/utils/metricChange';
import Chart from './Chart';
import { metricsTooltipFormatter } from './tooltip';
import { getRowId } from './utils';

const PieChart = ({ results, config, segment, segments, selectedMetric }) => {
  const $t = useFormatMessage();
  const { goalNames, loading: goalNamesLoading } = useMatomoGoalNames();
  const { currency, ready } = useWebsiteCurrency();
  const displayCurrency = ready ? currency : null;
  const metrics = config?.metrics || [];
  const dimensions = config?.dimensions || [];
  const dimensionIds = dimensions.map(d => d.name);
  const { hideLabels, shareOfTotal, pieChartHeight = 300 } = config?.settings || {};

  const option = useMemo(() => {
    const result = results?.find(r => r.segmentId === segment.id);

    if (!result) {
      return {};
    }

    const metric = metrics.find(m => m.name === selectedMetric);
    const useShareOfTotal = shareOfTotal && metricSupportsShareOfTotal(metric);

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params) => metricsTooltipFormatter({
          $t,
          segments,
          metrics,
          results,
          dimensions,
          rowId: params.name,
          goalNames,
          goalNamesLoading,
          currency: displayCurrency,
        }),
      },
      series: [{
        type: 'pie',
        radius: [0, '60%'],
        top: 10,
        bottom: 10,
        name: segment.title || segment.id,
        data: result.data.map(d => ({
          name: getRowId(d, dimensionIds),
          value: d[selectedMetric],
        })),
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 1,
        },
        label: hideLabels ? undefined : {
          overflow: 'break',
          formatter: (args) => {
            const { name, value, percent } = args;
            const row = result.data.find(d => getRowId(d, dimensionIds) === name);
            const label = `${dimensions.map(d => getDimensionValue({ $t, value: row[d.name], dimension: d, goalNames, goalNamesLoading })).join(' ')}: ${useShareOfTotal
              ? getFormatter('percent', { maximumFractionDigits: getMetricPrecision(metric, 'display') })(percent / 100)
              : getFormatter(metric.type, { currency: displayCurrency, maximumFractionDigits: getMetricPrecision(metric, 'display') })(value)}`;

            return label;
          },
        },
      }],
    };
  }, [results, dimensions, metrics, segment, selectedMetric, shareOfTotal, goalNames, goalNamesLoading, displayCurrency, $t, hideLabels, segments]);

  return (
    <Chart
      option={option}
      height={pieChartHeight}
    />
  );
};

const getColSpan = (width, segmentsLength) => {
  let colSpan = 24;
  if (width > 800 && segmentsLength % 2 === 0) {
    colSpan = 12;
  }
  if (width > 1200 && segmentsLength % 3 === 0) {
    colSpan = 8;
  }

  return colSpan;
};

const PieChartResult = ({ results, config, segments, selectedMetric, width }) => (
  <Row gutter={[16, 16]}>
    {segments?.map(segment => (
      <Col key={segment.id} span={getColSpan(width, segments.length)}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 8, padding: '8px 0' }}>
          <SegmentTag segment={segment} showDateRange />
        </div>
        <PieChart results={results} config={config} segment={segment} segments={segments} selectedMetric={selectedMetric} />
      </Col>
    ))}
  </Row>
);

export default PieChartResult;
